import { useEffect, useState, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePlayer } from '../context/PlayerContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CommentSection } from '../components/SocialFeatures';
import { 
  TrackCover, 
  TrackInfo, 
  TrackActions, 
  BuyTrackCard,
  TokenPurchaseCard,
  TokenInfoBox,
  TrackAnalytics
} from '../components/track';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TrackDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTrack, isPlaying, playTrack, pause } = usePlayer();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  
  const [track, setTrack] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [royalties, setRoyalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenAmount, setTokenAmount] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  const pollIntervalRef = useRef(null);

  // Function to refresh track data (for live play count updates)
  const refreshTrackData = useCallback(async () => {
    try {
      const trackRes = await axios.get(`${API_URL}/tracks/${id}`);
      setTrack(prev => ({...prev, plays: trackRes.data.plays}));
    } catch (err) {
      logger.error('Failed to refresh track data:', err);
    }
  }, [id]);

  // Poll for play count updates when viewing the current playing track
  useEffect(() => {
    const isViewingCurrentTrack = currentTrack?.id === id && isPlaying;
    
    if (isViewingCurrentTrack) {
      // Start polling every 5 seconds
      pollIntervalRef.current = setInterval(refreshTrackData, 5000);
    } else {
      // Clear polling when not playing this track
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [currentTrack?.id, id, isPlaying, refreshTrackData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [trackRes, tokenRes] = await Promise.all([
          axios.get(`${API_URL}/tracks/${id}`),
          axios.get(`${API_URL}/tokens/track/${id}`)
        ]);
        setTrack(trackRes.data);
        setTokenInfo(tokenRes.data);
        
        // Fetch royalties for this track (public endpoint)
        try {
          const royaltiesRes = await axios.get(`${API_URL}/royalties/track/${id}`);
          setRoyalties(royaltiesRes.data || []);
        } catch (err) {
          // Royalties may require auth or may not exist yet
          setRoyalties([]);
        }
        
        if (isAuthenticated) {
          try {
            const purchasesRes = await axios.get(`${API_URL}/purchases`);
            const purchased = purchasesRes.data.some(p => 
              (p.item_type === 'track' && p.item_id === id) ||
              (p.item_type === 'album' && p.item_id === trackRes.data.album_id)
            );
            setHasPurchased(purchased);
            
            const playlistsRes = await axios.get(`${API_URL}/playlists`);
            setPlaylists(playlistsRes.data);
          } catch (err) {
            logger.error('Failed to fetch user data:', err);
          }
        }
      } catch (error) {
        logger.error('Failed to fetch track:', error);
        navigate('/music');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate, isAuthenticated]);

  const isCurrentTrack = currentTrack?.id === track?.id;

  const handlePlay = () => {
    if (isCurrentTrack && isPlaying) {
      pause();
    } else if (track) {
      playTrack(track);
    }
  };

  const handleAddTrackToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    
    setAddingToCart(true);
    try {
      await addToCart(track.id, 'track');
      toast.success('Track added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleAddTokensToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    if (tokenAmount > tokenInfo.available_tokens) {
      toast.error('Not enough tokens available');
      return;
    }
    
    setAddingToCart(true);
    try {
      await addToCart(track.id, 'token', 1, tokenAmount);
      toast.success(`${tokenAmount}% ownership tokens added to cart`);
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleAddToPlaylist = async (playlistId) => {
    try {
      await axios.post(`${API_URL}/playlists/${playlistId}/tracks/${track.id}`);
      toast.success('Added to playlist');
      setShowPlaylistDialog(false);
    } catch (error) {
      toast.error('Failed to add to playlist');
    }
  };

  const handleDownload = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to download');
      navigate('/login');
      return;
    }
    
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/download/track/${track.id}`);
      const link = document.createElement('a');
      link.href = response.data.download_url;
      link.download = response.data.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Purchase this track to download');
      } else {
        toast.error('Download failed');
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!track) return null;

  return (
    <div className="main-content pt-24" data-testid="track-detail-page">
      <div className="container py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Track Info */}
          <div>
            <TrackCover 
              track={track}
              isCurrentTrack={isCurrentTrack}
              isPlaying={isPlaying}
              onPlay={handlePlay}
            />

            <TrackInfo track={track} />

            <TrackActions
              track={track}
              isAuthenticated={isAuthenticated}
              hasPurchased={hasPurchased}
              downloading={downloading}
              playlists={playlists}
              showPlaylistDialog={showPlaylistDialog}
              setShowPlaylistDialog={setShowPlaylistDialog}
              onAddToPlaylist={handleAddToPlaylist}
              onDownload={handleDownload}
            />

            <BuyTrackCard
              track={track}
              hasPurchased={hasPurchased}
              addingToCart={addingToCart}
              onAddToCart={handleAddTrackToCart}
              onDownload={handleDownload}
              downloading={downloading}
            />
          </div>

          {/* Right - Token Purchase & Analytics */}
          <div>
            <TrackAnalytics 
              track={track}
              tokenInfo={tokenInfo}
              royalties={royalties}
            />
            
            <TokenPurchaseCard
              track={track}
              tokenInfo={tokenInfo}
              tokenAmount={tokenAmount}
              setTokenAmount={setTokenAmount}
              addingToCart={addingToCart}
              onAddTokensToCart={handleAddTokensToCart}
            />
            <TokenInfoBox />
          </div>
        </div>

        {/* Comments Section */}
        <div className="mt-12 max-w-2xl">
          <div className="glass-card p-6">
            <CommentSection contentType="track" contentId={track.id} />
          </div>
        </div>
      </div>
    </div>
  );
}
